import React from "react";
import { NavLink, Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import {
  getMerchantDefaultRoute,
  useMerchantAuthActions,
  useMerchantAuthUser,
  useMerchantHasPermission,
  useMerchantPermissions,
} from "../auth/MerchantAuthContext";
import {
  MerchantBellIcon,
  MerchantDashboardIcon,
  MerchantMoreIcon,
  MerchantSearchIcon,
  MerchantSettlementsIcon,
  MerchantSignOutIcon,
  MerchantTransactionsIcon,
} from "../components/MerchantIcons";
import MerchantMobileShell from "../components/MerchantMobileShell";
import MerchantSidebarBanner from "../components/MerchantSidebarBanner";
import {
  getMerchantApiActivitySnapshot,
  subscribeMerchantApiActivity,
} from "../data/merchantApi";
import { merchantNavItems, merchantPortalMeta } from "../data/merchantData";
import { pickMerchantField, toMerchantString } from "../data/merchantLegacyUtils";
import MerchantDashboardPage from "../pages/MerchantDashboardPage";
import MerchantForbiddenPage from "../pages/MerchantForbiddenPage";
import MerchantNotFoundPage from "../pages/MerchantNotFoundPage";
import MerchantOutletsPage from "../pages/MerchantOutletsPage";
import MerchantSettingsPage from "../pages/MerchantSettingsPage";
import MerchantSettlementsPage from "../pages/MerchantSettlementsPage";
import MerchantShortcutPage from "../pages/MerchantShortcutPage";
import MerchantTransactionDetailPage from "../pages/MerchantTransactionDetailPage";
import MerchantTransactionsPage from "../pages/MerchantTransactionsPage";

const MERCHANT_THEME_KEY = "merchant_portal_theme";
const MERCHANT_MOBILE_MEDIA_QUERY = "(max-width: 900px)";
const MERCHANT_SIDEBAR_BANNER_URL =
  "https://ib.anext.com.sg/mkyb/product-intro?scene=pos_loan_onboarding_apply&partnerId=UNIWEB";
const MERCHANT_BOTTOM_NAV_ITEMS = [
  {
    key: "dashboard",
    label: "Dashboard",
    path: "/dashboard",
    icon: MerchantDashboardIcon,
  },
  {
    key: "transactions",
    label: "Transactions",
    path: "/transactions",
    icon: MerchantTransactionsIcon,
  },
  {
    key: "settlements",
    label: "Settlements",
    path: "/settlements",
    icon: MerchantSettlementsIcon,
  },
  {
    key: "more",
    label: "More",
    path: "/more",
    icon: MerchantMoreIcon,
  },
];

function getAvatarText(user) {
  const source = toMerchantString(
    pickMerchantField(user, ["businessName", "lastName", "firstName"]),
    merchantPortalMeta.avatar,
  );

  return source.slice(0, 2).toUpperCase();
}

function getInitialDarkMode() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(MERCHANT_THEME_KEY) === "dark";
}

function useMerchantIsMobile() {
  const [isMobile, setIsMobile] = React.useState(function getInitialMobileState() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(MERCHANT_MOBILE_MEDIA_QUERY).matches;
  });

  React.useEffect(function bindMobileMediaQuery() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(MERCHANT_MOBILE_MEDIA_QUERY);

    function handleChange(event) {
      setIsMobile(event.matches);
    }

    setIsMobile(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", handleChange);

      return function cleanup() {
        mediaQueryList.removeEventListener("change", handleChange);
      };
    }

    mediaQueryList.addListener(handleChange);

    return function cleanup() {
      mediaQueryList.removeListener(handleChange);
    };
  }, []);

  return isMobile;
}

function getMerchantRouteMeta(pathname) {
  const normalizedPath = toMerchantString(pathname);

  if (
    normalizedPath.indexOf("/transactions/") === 0 &&
    normalizedPath !== "/transactions"
  ) {
    return {
      title: "Transaction Details",
      drawerKey: "transactions",
      bottomNavKey: "transactions",
    };
  }

  if (normalizedPath === "/more") {
    return {
      title: "More",
      drawerKey: "",
      bottomNavKey: "more",
    };
  }

  if (normalizedPath === "/forbidden") {
    return {
      title: "Access Restricted",
      drawerKey: "",
      bottomNavKey: "more",
    };
  }

  const matchedNavItem = merchantNavItems.find(function findNavItem(item) {
    return normalizedPath === item.path || normalizedPath.indexOf(item.path + "/") === 0;
  });

  if (matchedNavItem) {
    return {
      title: matchedNavItem.title,
      drawerKey: matchedNavItem.key,
      bottomNavKey:
        matchedNavItem.key === "dashboard" ||
        matchedNavItem.key === "transactions" ||
        matchedNavItem.key === "settlements"
          ? matchedNavItem.key
          : "more",
    };
  }

  return {
    title: "Merchant Portal",
    drawerKey: "",
    bottomNavKey: "more",
  };
}

function MerchantPageRoute({ component, permission, ...rest }) {
  const hasPermission = useMerchantHasPermission(permission);

  return (
    <Route
      {...rest}
      render={function renderMerchantPage(props) {
        return hasPermission ? (
          React.createElement(component, props)
        ) : (
          <Redirect to="/forbidden" />
        );
      }}
    />
  );
}

function MerchantLayout() {
  const history = useHistory();
  const location = useLocation();
  const { logout } = useMerchantAuthActions();
  const merchantUser = useMerchantAuthUser();
  const merchantPermissions = useMerchantPermissions();
  const [isDarkMode, setIsDarkMode] = React.useState(getInitialDarkMode);
  const isMobile = useMerchantIsMobile();
  const [apiActivity, setApiActivity] = React.useState(getMerchantApiActivitySnapshot);

  React.useEffect(
    function syncMerchantTheme() {
      if (typeof window === "undefined") {
        return;
      }

      window.localStorage.setItem(MERCHANT_THEME_KEY, isDarkMode ? "dark" : "light");
    },
    [isDarkMode],
  );

  React.useEffect(function bindMerchantApiActivity() {
    return subscribeMerchantApiActivity(function handleMerchantApiActivity(snapshot) {
      setApiActivity(snapshot);
    });
  }, []);

  const visibleNavItems = merchantNavItems.filter(function filterNavItem(item) {
    return merchantPermissions.indexOf(item.permission) >= 0;
  });
  const routeMeta = getMerchantRouteMeta(location.pathname);
  const merchantAccountName = toMerchantString(
    pickMerchantField(merchantUser, ["businessName", "lastName", "name"]),
    "Merchant Account",
  );
  const merchantAccountId = toMerchantString(
    pickMerchantField(merchantUser, ["businessId", "id"]),
    "",
  );
  function handleLogout() {
    logout();
    history.push("/login");
  }

  function renderMerchantApiOverlay(className) {
    if (!apiActivity.isLoading) {
      return null;
    }

    return (
      <div className={"merchant-request-overlay " + className}>
        <div className="merchant-request-modal">
          <span className="merchant-request-spinner merchant-request-spinner-large" />
          <div className="merchant-request-modal-title">Syncing merchant data</div>
          <div className="merchant-request-modal-subtitle">
            The latest merchant information is being requested from the API.
          </div>
        </div>
      </div>
    );
  }

  function renderMerchantRoutes() {
    return (
      <Switch>
        <MerchantPageRoute
          exact
          path="/dashboard"
          permission="dashboard"
          component={MerchantDashboardPage}
        />
        <MerchantPageRoute
          exact
          path="/transactions/:transactionId"
          permission="transactions"
          component={MerchantTransactionDetailPage}
        />
        <MerchantPageRoute
          exact
          path="/transactions"
          permission="transactions"
          component={MerchantTransactionsPage}
        />
        <MerchantPageRoute
          exact
          path="/settlements"
          permission="settlements"
          component={MerchantSettlementsPage}
        />
        <MerchantPageRoute
          exact
          path="/outlets"
          permission="outlets"
          component={MerchantOutletsPage}
        />
        <Route exact path="/reports">
          <Redirect to="/dashboard" />
        </Route>
        <Route exact path="/users">
          <Redirect to="/dashboard" />
        </Route>
        <MerchantPageRoute
          exact
          path="/settings"
          permission="settings"
          component={MerchantSettingsPage}
        />
        <Route exact path="/more" component={MerchantShortcutPage} />
        <Route exact path="/forbidden" component={MerchantForbiddenPage} />
        <Route exact path="/">
          <Redirect to={getMerchantDefaultRoute(merchantUser)} />
        </Route>
        <Route component={MerchantNotFoundPage} />
      </Switch>
    );
  }

  if (isMobile) {
    return (
      <React.Fragment>
        <MerchantMobileShell
          activeBottomNavKey={routeMeta.bottomNavKey}
          activeDrawerKey={routeMeta.drawerKey}
          apiOverlay={renderMerchantApiOverlay("merchant-request-overlay-content")}
          avatarText={getAvatarText(merchantUser)}
          bannerLinkPath={MERCHANT_SIDEBAR_BANNER_URL}
          bottomNavItems={MERCHANT_BOTTOM_NAV_ITEMS}
          currentPath={location.pathname}
          isDarkMode={isDarkMode}
          merchantAccountId={merchantAccountId}
          merchantAccountName={merchantAccountName}
          navItems={visibleNavItems}
          onLogout={handleLogout}
          onToggleDarkMode={function toggleDarkMode() {
            setIsDarkMode(!isDarkMode);
          }}
          pageLabel={merchantAccountName}
          pageTitle={routeMeta.title}
        >
          {renderMerchantRoutes()}
        </MerchantMobileShell>
      </React.Fragment>
    );
  }

  return (
    <div className={"merchant-shell merchant-portal-shell" + (isDarkMode ? " merchant-dark" : "")}>
      <aside className="merchant-sidebar">
        <div className="merchant-sidebar-brand">
          <div className="merchant-logo-mark merchant-logo-mark-small">
            <img
              src={process.env.PUBLIC_URL + "/merchant-login-logo.webp"}
              alt="Uniweb"
            />
          </div>
          <div>
            <div className="merchant-brand-name">Uniweb Pte. Ltd.</div>
            <div className="merchant-brand-sub">Merchant Portal</div>
          </div>
        </div>
        <div className="merchant-account-card">
          <div className="merchant-account-label">Merchant Account</div>
          <div className="merchant-account-name">{merchantAccountName}</div>
          <div className="merchant-account-id">{merchantAccountId}</div>
        </div>
        <nav className="merchant-sidebar-nav">
          {visibleNavItems.map(function renderNavItem(item) {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.key}
                to={item.path}
                className="merchant-nav-item"
                activeClassName="active"
              >
                <Icon />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="merchant-sidebar-banner">
          <MerchantSidebarBanner linkPath={MERCHANT_SIDEBAR_BANNER_URL} />
        </div>
        <div className="merchant-sidebar-footer">
          <button
            className="merchant-toggle-row"
            type="button"
            onClick={function handleThemeToggle() {
              setIsDarkMode(!isDarkMode);
            }}
          >
            <span className={"merchant-toggle-switch" + (isDarkMode ? " on" : "")}>
              <span className="merchant-toggle-knob" />
            </span>
            <span>{isDarkMode ? "Light mode" : "Dark mode"}</span>
          </button>
          <button className="merchant-sign-out-row" type="button" onClick={handleLogout}>
            <MerchantSignOutIcon />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="merchant-main">
        <header className="merchant-header">
          <div className="merchant-header-title">{routeMeta.title}</div>
          <div className="merchant-search-box">
            <MerchantSearchIcon />
            <input type="text" placeholder="Search transactions, outlets…" />
          </div>
          <div className="merchant-notification-wrap">
            <button className="merchant-icon-button" type="button">
              <MerchantBellIcon />
            </button>
            <span className="merchant-notification-dot" />
          </div>
          <div className="merchant-avatar">{getAvatarText(merchantUser)}</div>
        </header>

        <div className="merchant-content">{renderMerchantRoutes()}</div>
        {renderMerchantApiOverlay("merchant-request-overlay-content")}

        <footer className="merchant-footer">
          <span>{merchantPortalMeta.footer}</span>
          <span>{merchantPortalMeta.systemStatus}</span>
        </footer>
      </main>
    </div>
  );
}

export default MerchantLayout;
